import { createContext, useContext } from 'react';
import type {
  DeploymentServingState,
  ComponentsOverview,
  Environment,
  EnvironmentOverview,
  GitConnectionResponse,
  OnboardingPlan,
  Project,
  ProjectApplication,
  ProjectDeployment,
  RecoveryPoint,
  RestoreRun,
  Server,
  ApplicationErrorGroup,
  EnvironmentHealthView,
  RepositoryAccess,
  WordPressSite,
} from '../../api';
import type { Resource } from '../../hooks';
import type { SetupProgress } from '../../components/project/projectHealth';

/**
 * ProjectContext is what the project shell knows and every section reads.
 * The shell loads each record once — the project, its environments, the
 * selected environment's picture, its components, deployments, recovery
 * points and setup plan — so switching sections re-renders rather than
 * re-fetches, and two sections can never show different answers for the
 * same environment.
 */
export interface ProjectContextValue {
  projectId: string;
  project: Project;
  environments: Environment[];
  /** The environment every environment-scoped section is about. */
  environment: Environment | null;
  selectEnvironment: (environmentId: string) => void;
  /** A link to another section that keeps the environment context. */
  sectionPath: (section: string, params?: Record<string, string>) => string;

  servers: Resource<{ servers: Server[] }>;
  applications: ProjectApplication[];
  /** Applications including removed ones: history still names them. */
  historyApplications: ProjectApplication[];
  reloadApplications: () => void;

  environmentOverview: Resource<EnvironmentOverview | null>;
  components: Resource<ComponentsOverview | null>;
  deployments: Resource<{ deployments: ProjectDeployment[]; serving?: Record<string, DeploymentServingState> }>;
  recoveryPoints: Resource<{ recovery_points: RecoveryPoint[]; incomplete_restore?: RestoreRun }>;
  /** The environment's health as the server derived it (CO-95). */
  environmentHealth: Resource<EnvironmentHealthView | null>;
  /** Which credential reads the repository (CO-98). */
  repositoryAccess: Resource<RepositoryAccess | null>;
  plan: Resource<OnboardingPlan | null>;
  gitConnection: Resource<GitConnectionResponse>;

  isHeadless: boolean;
  /** A WordPress / WooCommerce project (CO-60): its own sections and words. */
  isWordPress: boolean;
  /** The selected environment's WordPress site; null when not connected yet
   * (and always null for a Magento project). */
  wordpressSite: Resource<WordPressSite | null>;
  /** Grouped application errors of the selected environment (WordPress). */
  errorGroups: Resource<{ groups: ApplicationErrorGroup[] | null }>;
  /** Whether the project's primary application — Magento, or the WordPress
   * site — is placed on a server and, for Magento, its setup has actually
   * run there. Approving a plan places Magento without setting anything up
   * (CO-73), so a placement alone never enables a deployment. */
  placedPrimary: boolean;
  /** The environment's setup checklist and where its plan stands. */
  setup: SetupProgress;
  /** Components the environment has no placement for yet. */
  unplacedComponentIds: string[];

  serverName: (serverId: string) => string;
  applicationNames: (ids?: string[]) => string;

  /** Open the deployment dialog, preselecting these components (all when empty). */
  openDeploy: (componentIds: string[]) => void;
  /** Re-read everything a run on the server can have changed. */
  refresh: () => void;
}

export const ProjectContext = createContext<ProjectContextValue | null>(null);

export function useProject(): ProjectContextValue {
  const value = useContext(ProjectContext);
  if (!value) throw new Error('useProject must be used inside the project shell');
  return value;
}
